/**
 * What the sync indicator (SyncStatus.jsx) shows, from the op queue's state in the food store and the
 * browser's connection (docs/local-first-food-data.md, phase 5):
 *
 * | State | Shows |
 * |---|---|
 * | online, nothing pending | nothing |
 * | offline | "Offline", plus "N unsent" |
 * | sending | "Sending N..." |
 * | paused for auth | "Sign in again to send N changes" |
 * | rejected entries | "N changes couldn't be saved", and the list |
 * | not persistent | "Changes won't survive a reload" |
 *
 * Beyond the table, it says when the browser holds no copy of the budget's food data and the snapshot
 * failed: with no legacy read to fall back on, every food screen is otherwise empty without a word.
 *
 * N is the signed-in user's ops not yet sent, across budgets: the queue's count (`sync.unsent`), or the
 * budget shown's pending ops when they are more, as they are for the moment between a tick and its
 * write to the browser's queue.
 */

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

/** The inputs of the indicator, from the whole state. Plain values and the store's own `rejected`, so shallowEqual holds. */
export const selectSyncStatus = (state) => {
	const food = state.food || {};
	const sync = food.sync || {};
	const pending = (food.pending || []).length;
	return {
		offline: !!state.dashboard && state.dashboard.isOnline === false,
		unsent: Math.max(Number(sync.unsent) || 0, pending),
		sending: !!(sync.anySending || sync.sending),
		paused: !!sync.paused,
		lastError: sync.lastError || null,
		persistent: food.persistent !== false && sync.persistent !== false,
		rejected: food.rejected || [],
		loadError: food.status === 'error' ? (food.error || '') : null,
	};
};

/**
 * The one line about unsent changes, or null when there is nothing to say: offline first, since
 * nothing can be sent or signed in to then; then a pause for sign-in, a request in flight, and ops
 * waiting for their next try.
 */
export const syncStatusLine = ({ offline, unsent, sending, paused }) => {
	if(offline) {
		return unsent ? `Offline · ${unsent} unsent` : 'Offline';
	}
	if(!unsent) {
		return null;
	}
	if(paused) {
		return `Sign in again to send ${plural(unsent, 'change', 'changes')}`;
	}
	if(sending) {
		return `Sending ${unsent}...`;
	}
	return `${unsent} unsent`;
};

/** The heading over the changes the server refused. */
export const rejectedHeading = (count) => `${plural(count, 'change', 'changes')} couldn't be saved`;

export const NOT_PERSISTENT = "Changes won't survive a reload";

export const LOAD_FAILED = "Couldn't load meals and groceries";
